import {
  forecastToneColor,
  forecastToneColorSoft,
} from '@/pages/dashboard/forecastScoreMath'

interface ScorePeriod {
  score: number | null
  band?: { label: string; tone: string } | null
  is_provisional?: boolean
}

/**
 * A period's forecast score, as a coloured disc for the corner of that
 * period's column header. Colour is the band, so a row of headers reads as a
 * run of traffic lights before any number is read.
 *
 * Rounded to whole numbers: at this size 61.9 doesn't fit legibly, and the
 * precise figure is the dashboard card's job. The exact value stays in the
 * title for anyone who wants it.
 *
 * A period still running is scored on what has elapsed so far, and is shown
 * filled in its band's softer shade rather than solid — same colour, visibly
 * not final.
 */
export default function ForecastScoreBadge({ period }: { period?: ScorePeriod | null }) {
  if (!period || period.score === null || period.score === undefined) return null

  const tone = period.band?.tone
  const provisional = !!period.is_provisional

  const title = [
    period.band?.label ? `${period.band.label} — ${period.score}` : `Score ${period.score}`,
    provisional ? '(so far this period)' : null,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <span
      title={title}
      className="flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold tabular-nums"
      style={
        provisional
          ? {
              backgroundColor: forecastToneColorSoft(tone),
              color: forecastToneColor(tone),
            }
          : { backgroundColor: forecastToneColor(tone), color: '#fff' }
      }
    >
      {Math.round(period.score)}
    </span>
  )
}
